import { useState, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { API_URL } from "../config/constants";
import carImage from "../assets/images/top_down_car.png";
import "../assets/css/WelcomeOverlay.css";

const SESSION_KEY = "autoassure_welcome_shown";

/**
 * Phase machine:
 *   idle       → not yet started
 *   car-in     → car slides from left to center (1.8s) spinning out
 *   smoke      → smoke expands radially (0.8s overlap)
 *   form-show  → form materializes from the smoke cloud
 *   submitting → API call in-flight
 *   thankyou   → brief confirmation
 *   form-hide  → form dissolves back to smoke
 *   car-out    → car drifts off to the right spinning
 *   done       → unmount
 */
const WelcomeOverlay = () => {
  const [phase, setPhase] = useState("idle");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });

  // ── Show once per session ──
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) {
      setPhase("done");
      return;
    }
    const t = setTimeout(() => setPhase("car-in"), 800);
    return () => clearTimeout(t);
  }, []);

  // ── Phase transitions ──
  useEffect(() => {
    let t;
    switch (phase) {
      case "car-in":
        // Smoke starts following the car almost immediately
        t = setTimeout(() => setPhase("smoke"), 100);
        break;
      case "smoke":
        // Form materializes after the car finishes sliding (1.6s total slide)
        t = setTimeout(() => setPhase("form-show"), 1600);
        break;
      case "thankyou":
        t = setTimeout(() => setPhase("form-hide"), 2000);
        break;
      case "form-hide":
        t = setTimeout(() => setPhase("car-out"), 600);
        break;
      case "car-out":
        t = setTimeout(() => {
          sessionStorage.setItem(SESSION_KEY, "true");
          setPhase("done");
        }, 1200);
        break;
      default:
        break;
    }
    return () => clearTimeout(t);
  }, [phase]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, "true");
    setPhase("form-hide");
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, phone, email, message } = formData;
    if (!name.trim() || !phone.trim() || !email.trim()) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    setPhase("submitting");
    try {
      const res = await fetch(`${API_URL}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          enquiryType: "Buying Car",
          message: message.trim() || "Submitted via welcome prompt"
        })
      });
      if (!res.ok) throw new Error();
      toast.success("Thank you! We'll be in touch soon.");
      setPhase("thankyou");
    } catch {
      toast.error("Something went wrong. Please try again.");
      setPhase("form-show");
    }
  };

  // ── Generate smoke puffs radially for top-down view ──
  const smokePuffs = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        const angle = Math.random() * Math.PI * 2;
        const dist = 50 + Math.random() * 250;
        return {
          id: i,
          left: `calc(50% - 30px)`, // center horizontally
          top: `calc(50% - 30px)`,  // center vertically
          width: `${60 + Math.random() * 80}px`,
          height: `${60 + Math.random() * 80}px`,
          tx: `${Math.cos(angle) * dist}px`,
          ty: `${Math.sin(angle) * dist}px`,
          scale: 1.5 + Math.random() * 3,
          delay: `${Math.random() * 0.6}s`,
          duration: `${1.2 + Math.random() * 1}s`
        };
      }),
    []
  );

  // ── Tire marks (donuts) ──
  const tireMarks = useMemo(
    () => [
      { id: 1, width: "150px", height: "150px", rotate: "45deg" },
      { id: 2, width: "180px", height: "120px", rotate: "-30deg" },
      { id: 3, width: "220px", height: "220px", rotate: "110deg" },
    ],
    []
  );

  if (phase === "done" || phase === "idle") return null;

  const isActive = phase !== "done";
  const isExiting = phase === "car-out";
  const showCar = true; // always visible once started
  const carDriftIn = phase === "car-in" || phase === "smoke";
  const carDriftOut = phase === "car-out";
  const showSmoke = phase === "smoke" || phase === "form-show";
  const showForm =
    phase === "form-show" ||
    phase === "submitting" ||
    phase === "thankyou";
  const formDissolve = phase === "form-hide";
  const formMaterialize = showForm;

  return (
    <div
      className={`welcome-overlay${isActive ? " active" : ""}${isExiting ? " exiting" : ""}`}
    >
      {/* Dark background */}
      <div className="welcome-bg" />

      {/* Tire marks on arrival (donuts) */}
      <div className={`welcome-tire-marks${phase === "smoke" ? " show" : ""}`}>
        {tireMarks.map((mark) => (
          <span
            key={mark.id}
            className="tire-mark"
            style={{
              width: mark.width,
              height: mark.height,
              transform: `translate(-50%, -50%) rotate(${mark.rotate})`
            }}
          />
        ))}
      </div>

      {/* The car and its smoke trail */}
      {showCar && (
        <div
          className={`welcome-car-wrap${carDriftIn ? " drift-in" : ""}${carDriftOut ? " drift-out" : ""}`}
        >
          {/* Smoke follows the car's slide, but doesn't spin */}
          {(showSmoke || formDissolve) && (
            <div className="welcome-smoke-zone">
              {smokePuffs.map((p) => (
                <span
                  key={p.id}
                  className={`smoke-puff${showSmoke ? " animate" : ""}`}
                  style={{
                    left: p.left,
                    top: p.top,
                    width: p.width,
                    height: p.height,
                    "--tx": p.tx,
                    "--ty": p.ty,
                    "--scale": p.scale,
                    "--delay": p.delay,
                    "--duration": p.duration
                  }}
                />
              ))}
            </div>
          )}

          <div className="welcome-car-spin">
            <img src={carImage} alt="" className="welcome-car-img" />
          </div>
        </div>
      )}

      {/* Form — materializes from smoke, dissolves back */}
      {(showForm || formDissolve) && (
        <div className="welcome-form-wrap">
          <div
            className={`welcome-form-card${formMaterialize ? " materialize" : ""}${formDissolve ? " dissolve" : ""}`}
          >
            <button
              type="button"
              className="welcome-close"
              onClick={handleDismiss}
              aria-label="Close"
            >
              ✕
            </button>

            {phase === "thankyou" ? (
              <div className="welcome-thankyou">
                <div className="welcome-thankyou-icon">🏎️</div>
                <h3>You're all set!</h3>
                <p>Our team will reach out to you shortly.</p>
              </div>
            ) : (
              <>
                <h3>Welcome to Auto Assure</h3>
                <p className="welcome-subtitle">
                  Tell us what you're looking for and we'll find the
                  perfect ride for you.
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="welcome-field">
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="name"
                    />
                  </div>
                  <div className="welcome-field">
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                    />
                  </div>
                  <div className="welcome-field">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                    />
                  </div>
                  <div className="welcome-field">
                    <textarea
                      name="message"
                      placeholder="What are you looking for? (e.g. BMW X5, budget 150k AED...)"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                    />
                  </div>
                  <button
                    type="submit"
                    className="welcome-submit"
                    disabled={phase === "submitting"}
                  >
                    {phase === "submitting" ? "Sending..." : "Let's Go 🚀"}
                  </button>
                </form>

                <button
                  type="button"
                  className="welcome-skip"
                  onClick={handleDismiss}
                >
                  Skip for now
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WelcomeOverlay;
